import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  Download,
  ExternalLink,
  Share2,
  Check,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { agentApi, type OwnerShare } from '@/api/agent';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

/**
 * Confidentialité — real CRUD, claude.ai parity, on the aikka square DA (5px
 * radii; no `rounded-full`, no named radii).
 *
 * Three groups mirror Claude's Privacy panel:
 *  - intro + two policy links (→ the real /privacy and /legal routes);
 *  - **Préférences**: two persisted toggles — location metadata and
 *    AI-training opt-in — read/written through GET/PUT /agent/preferences
 *    (boolean columns on UserPreference, both default OFF);
 *  - **Vos données**: export the user's data as a JSON download
 *    (GET /agent/export), manage public conversation shares (list + revoke via
 *    /agent/shares), and an honest note that public artefact sharing has no
 *    backend yet (no dead "Gérer" button).
 *
 * Every control is wired to a real endpoint; the only "coming soon" is the
 * artefact-sharing note, stated plainly rather than mocked.
 */

type ToggleKey = 'locationMetadata' | 'aiTrainingOptin';
type SaveState = 'idle' | 'saving' | 'error';

const PREFERENCES_KEY = ['agent', 'preferences'] as const;
const SHARES_KEY = ['agent', 'shares'] as const;

/** Trigger a client-side download of a Blob without leaving the page. Guarded so
 *  it degrades gracefully in environments (jsdom) without object-URL support. */
function downloadBlob(blob: Blob, filename: string): void {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Accessible square switch (role="switch"), on the aikka DA. */
function PrivacyToggle({
  id,
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p id={`${id}-label`} className="text-sm font-medium">
          {label}
        </p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        data-testid={id}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-5 w-9 shrink-0 rounded-control border transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${
          checked ? 'border-primary bg-primary' : 'border-border bg-input'
        }`}
      >
        <span
          aria-hidden="true"
          className={`absolute top-0.5 h-4 w-4 rounded-control bg-background shadow-sm transition-transform ${
            checked ? 'translate-x-[18px]' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}

/** The expandable "Conversations partagées → Gérer" manager: list + revoke. */
function SharedConversationsManager() {
  const { t } = useTranslation('dashboard');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: SHARES_KEY,
    queryFn: () => agentApi.listShares().then((r) => r.data.shares),
    enabled: open,
  });

  const shares = data ?? [];

  const handleRevoke = async (share: OwnerShare) => {
    setRevokeError(false);
    setRevokingId(share.shareId);
    try {
      await agentApi.revokeShare(share.shareId);
      await queryClient.invalidateQueries({ queryKey: SHARES_KEY });
    } catch {
      setRevokeError(true);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <Share2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {t('settings.data.sharedConvTitle')}
          </p>
          <p className="text-xs text-muted-foreground">{t('settings.data.sharedConvBody')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          aria-expanded={open}
          data-testid="privacy-shares-manage"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? t('settings.data.hide') : t('settings.data.manage')}
        </Button>
      </div>

      {open && (
        <div className="mt-3" data-testid="privacy-shares-list">
          {isLoading ? (
            <p className="text-xs text-muted-foreground">{t('settings.data.sharedConvLoading')}</p>
          ) : isError ? (
            <p role="alert" className="text-xs text-destructive">
              {t('settings.data.sharedConvError')}
            </p>
          ) : shares.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('settings.data.sharedConvEmpty')}</p>
          ) : (
            <ul className="space-y-2">
              {shares.map((share) => (
                <li
                  key={share.shareId}
                  className="flex items-center justify-between gap-3 rounded-control border border-border bg-surface-1 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{share.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {share.active
                        ? t('settings.data.expiresOn', {
                            date: new Date(share.expiresAt).toLocaleDateString(),
                          })
                        : share.revokedAt
                          ? t('settings.data.revoked')
                          : t('settings.data.expired')}
                      {' · '}
                      {t('settings.data.views', { count: share.viewCount })}
                    </p>
                  </div>
                  {share.active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      disabled={revokingId === share.shareId}
                      data-testid={`privacy-share-revoke-${share.shareId}`}
                      onClick={() => handleRevoke(share)}
                    >
                      {revokingId === share.shareId
                        ? t('settings.data.revoking')
                        : t('settings.data.revoke')}
                    </Button>
                  ) : (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {share.revokedAt ? t('settings.data.revoked') : t('settings.data.expired')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {revokeError && (
            <p role="alert" className="mt-2 text-xs text-destructive">
              {t('settings.data.sharedConvError')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function SettingsPrivacySection() {
  const { t } = useTranslation('dashboard');

  const { data, isLoading } = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: () => agentApi.getPreferences().then((r) => r.data),
  });

  // Local mirror of the two toggles, seeded from the server (both OFF when the
  // response predates the privacy contract or the user has no row yet).
  const serverPrivacy = useMemo(
    () => ({
      locationMetadata: data?.privacy?.locationMetadata ?? false,
      aiTrainingOptin: data?.privacy?.aiTrainingOptin ?? false,
    }),
    [data],
  );
  const [toggles, setToggles] = useState(serverPrivacy);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  useEffect(() => setToggles(serverPrivacy), [serverPrivacy]);

  const persistToggle = async (key: ToggleKey, next: boolean) => {
    const previous = toggles[key];
    setToggles((t0) => ({ ...t0, [key]: next })); // optimistic
    setSaveState('saving');
    try {
      await agentApi.updatePreferences({ privacy: { [key]: next } });
      setSaveState('idle');
    } catch {
      setToggles((t0) => ({ ...t0, [key]: previous })); // revert
      setSaveState('error');
    }
  };

  const [exportState, setExportState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  const handleExport = async () => {
    setExportState('loading');
    try {
      const res = await agentApi.exportData();
      downloadBlob(res.data, `aikka-export-${new Date().toISOString().slice(0, 10)}.json`);
      setExportState('done');
      setTimeout(() => setExportState('idle'), 3000);
    } catch {
      setExportState('error');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">{t('settings.data.title')}</CardTitle>
        </div>
        <CardDescription>{t('settings.data.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* INTRO + LES TROIS LIENS DE POLITIQUE — et trois corrections
            (Notion, « Last push before going live » → Settings → Privacy).

            1. DEUX PAGES DÉDIÉES. « Comment nous protégeons vos données » menait
               à /privacy et « Comment nous utilisons vos données » à /legal :
               deux promesses précises ouvrant deux documents entiers, à charge
               pour le lecteur de retrouver le passage. Elles ont maintenant
               chacune leur page, qui remonte les sections concernées de la
               politique — sans réécrire une ligne de texte juridique
               (`pages/DataPage.tsx`).
            2. LA POLITIQUE COMPLÈTE REMONTE ICI. Elle était seule tout en bas
               du panneau, après les exports et les partages : « Remonter
               "privacy policy" ». Les trois liens forment un groupe, ils se
               lisent ensemble.
            3. NOUVEL ONGLET, LES TROIS. C'étaient des `<Link>` react-router :
               cliquer emportait l'opérateur hors des paramètres, réglages en
               cours compris. Un document de référence se lit à côté, pas à la
               place. */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{t('settings.data.intro')}</p>
          <div className="flex flex-col gap-1">
            {[
              { to: '/resources/privacy/data-protection', label: t('settings.data.protectLink') },
              { to: '/resources/privacy/data-use', label: t('settings.data.useLink') },
              { to: '/resources/privacy', label: t('settings.data.privacyLink') },
            ].map((link) => (
              <a
                key={link.to}
                href={link.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`privacy-link-${link.to.split('/').pop()}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                {link.label}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        {/* Préférences — persisted toggles */}
        <div className="space-y-4 border-t border-border pt-5">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">{t('settings.data.preferencesTitle')}</p>
            {saveState === 'saving' && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
            )}
            {saveState === 'error' && (
              <span role="alert" className="inline-flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                {t('settings.data.toggleError')}
              </span>
            )}
          </div>
          <PrivacyToggle
            id="privacy-toggle-location"
            label={t('settings.data.locationTitle')}
            description={t('settings.data.locationBody')}
            checked={toggles.locationMetadata}
            disabled={isLoading || saveState === 'saving'}
            onChange={(next) => persistToggle('locationMetadata', next)}
          />
          <PrivacyToggle
            id="privacy-toggle-training"
            label={t('settings.data.trainingTitle')}
            description={t('settings.data.trainingBody')}
            checked={toggles.aiTrainingOptin}
            disabled={isLoading || saveState === 'saving'}
            onChange={(next) => persistToggle('aiTrainingOptin', next)}
          />
        </div>

        {/* Vos données */}
        <div className="space-y-5 border-t border-border pt-5">
          <p className="text-sm font-semibold">{t('settings.data.yourDataTitle')}</p>

          {/* Export */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium">{t('settings.data.exportTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('settings.data.exportBody')}</p>
              {exportState === 'done' && (
                <span
                  role="status"
                  className="mt-1 inline-flex items-center gap-1 text-xs text-cobalt-ink"
                >
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('settings.data.exportDone')}
                </span>
              )}
              {exportState === 'error' && (
                <span
                  role="alert"
                  className="mt-1 inline-flex items-center gap-1 text-xs text-destructive"
                >
                  <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('settings.data.exportError')}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={exportState === 'loading'}
              data-testid="privacy-export-button"
              onClick={handleExport}
            >
              {exportState === 'loading' ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
              )}
              {exportState === 'loading'
                ? t('settings.data.exporting')
                : t('settings.data.exportAction')}
            </Button>
          </div>

          {/* Conversations partagées → Gérer */}
          <div className="border-t border-border pt-4">
            <SharedConversationsManager />
          </div>

          {/* « Artéfacts partagés » est retiré (Notion → Settings → Privacy :
              « retirer "shared artifeact" »). Le bloc décrivait, en deux lignes
              grises, l'état d'un partage public d'artéfacts qui n'existe pas :
              rien à régler, rien à révoquer, rien à consulter. Les partages qui
              existent VRAIMENT — les liens de conversation — sont gérés juste
              au-dessus, et eux se révoquent. */}
        </div>

      </CardContent>
    </Card>
  );
}
